import styled from '@emotion/styled';
import { NextPageContext } from 'next';
import App from 'next/app';
import Head from 'next/head';
import { NextRouter } from 'next/router';
import React, { ErrorInfo } from 'react';
import { Cookies, CookiesProvider } from 'react-cookie';
import { Provider as ReduxProvider } from 'react-redux';
import smoothscroll from 'smoothscroll-polyfill';
import '../../public/static/style/blank.css';
import MilkywireHandRegularWoff2 from '../assets/fonts/MilkywireHand-Regular.woff2';
import SharpGroteskBook20Woff2 from '../assets/fonts/SharpGroteskBook20.woff2';
import SharpGroteskBook25Woff2 from '../assets/fonts/SharpGroteskBook25.woff2';
import SharpGroteskMedium20Woff2 from '../assets/fonts/SharpGroteskMedium20.woff2';
import SharpGroteskMedium25Woff2 from '../assets/fonts/SharpGroteskMedium25.woff2';
import Loader from '../common-components/component-library/Loader';
import { FooterPagesItemType } from '../common-components/component-library/navigation/navTypes';
import CookieConsent from '../common-components/component-library/page-sections/CookieConsent';
import Footer from '../common-components/component-library/page-sections/Footer';
import Header from '../common-components/component-library/page-sections/Header';
import Fonts from '../common-components/component-library/style/Fonts';
import Normalize from '../common-components/component-library/style/Normalize';
import Hotjar from '../common-components/component-library/tracking/Hotjar';
import UnsupportedBrowserModal from '../common-components/component-library/UnsupportedBrowserModal';
import MetaHeader from '../common-components/contentful-elements/MetaHeader';
import {
  FooterBarServerProps,
  NavBarServerProps,
  processFooterBarData,
  processNavigationBarData,
  processStaticFooter,
} from '../common-components/contentful-elements/util/navigation';
import {
  IFooter,
  ILinkSection,
  IMetaHeader,
  INavigationBar,
  IStaticLink,
} from '../types/@types/generated/contentful';
import { LayoutProps } from '../types/LayoutTypes';
import { EVENTS, sendEvent } from '../utils/analytics';
import { getByUniqueSlug } from '../utils/api/contentful';
import { EXCLUDE_FROM_TRACKING } from '../utils/constants/excludeFromTracking';
import { getClient } from '../utils/contentfulSetup';
import easterEgg from '../utils/easterEgg';
import { ApiError, UrlError } from '../utils/error';
import '../utils/firebase';
import getMilkywireLogo from '../utils/getMilkywireLogo';
import isBrowser from '../utils/helpers/isBrowser';
import { EmbeddedProvider, getIsEmbedded } from '../utils/helpers/nativeApp';
import { appWithTranslation } from '../utils/i18n';
import log from '../utils/log';
import store from '../utils/redux/store';
import { colors } from '../utils/styleguide';
import AdjustTracking from '../utils/tracking/AdjustTracking';
import AmplitudeTracking from '../utils/tracking/AmplitudeTracking';
import FacebookTracking from '../utils/tracking/FacebookTracking';
import * as ga from '../utils/tracking/googleAnalyticsTracking';
import { captureException, getScopeContext } from '../utils/tracking/sentry/sentry';
import SentryTracking from '../utils/tracking/SentryTracking';
import UptimeTracking from '../utils/tracking/UptimeRumTracking';
import ErrorPage from './_error';

const Layout = styled.div<{ backgroundColor?: string }>`
  background-color: ${({ backgroundColor }) => backgroundColor ?? colors.white};
  position: relative;
  z-index: 0;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

type Props = {
  metaHeader?: IMetaHeader;
  navigationBar?: NavBarServerProps;
  footerBar?: FooterBarServerProps;
  mode: 'browser' | 'app';
  error?: Error | ApiError | UrlError | { error: number };
  router: NextRouter;
  addHotjarScript: boolean;
  layoutProps: LayoutProps;
  hasTracking: boolean;
};

type GetInitialProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: any;
  ctx: NextPageContext;
  router: NextRouter;
};

interface State {
  isLoading: boolean;
  mode: 'browser' | 'app';
}

const HOTJAR_PAGES = new Set();

class MyApp extends App<Props, unknown, State> {
  footerPages?: IStaticLink[];
  loadTimeout: NodeJS.Timeout = setTimeout(() => {}, 100);
  state: State = {
    isLoading: false,
    mode: 'browser',
  };
  private static footerPages: FooterPagesItemType[];

  constructor(props: Props) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    super(props as any);
    if (isBrowser()) {
      const utmTags = {
        utm_source: props.router.query.utm_source,
        utm_medium: props.router.query.utm_medium,
        utm_campaign: props.router.query.utm_campaign,
        utm_term: props.router.query.utm_term,
        utm_content: props.router.query.utm_content,
      };
      if (!localStorage.getItem('initial-utm-tags')) {
        localStorage.setItem('initial-utm-tags', JSON.stringify(utmTags));
      }
      if (utmTags.utm_campaign) {
        localStorage.setItem('latest-utm-tags', JSON.stringify(utmTags));
      }
    }
  }

  static getCookies(ctx: NextPageContext) {
    if (ctx && ctx.req && ctx.req.headers.cookie) {
      return new Cookies(ctx.req.headers.cookie);
    }

    return undefined;
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    if (state.mode === 'app') {
      return null;
    } else if (props.mode === 'app') {
      return {
        ...state,
        mode: props.mode,
      };
    }

    return null;
  }

  static async getInitialProps({ Component, ctx, router }: GetInitialProps) {
    try {
      let pageProps: {
        layoutProps?: unknown;
        namespacesRequired?: string[];
        page?: { fields: Record<string, unknown> };
      } = {
        layoutProps: undefined,
      };
      const mode = getIsEmbedded(router) ? 'app' : 'browser';

      if (Component.getInitialProps) {
        pageProps = await Component.getInitialProps(ctx);
      }

      if (!pageProps.namespacesRequired) {
        pageProps.namespacesRequired = [];
      }

      const addHotjarScript =
        process.env.NODE_ENV === 'production' && HOTJAR_PAGES.has(ctx.pathname);
      const hasTracking = !EXCLUDE_FROM_TRACKING.has(ctx.pathname);

      //Only set meta header if not present in page props
      let metaHeader;

      const isPerfPage =
        pageProps?.page?.fields?.tags &&
        Array.isArray(pageProps.page.fields.tags) &&
        pageProps.page.fields.tags.includes('Perf');

      if (!pageProps?.page?.fields?.metaHeader) {
        metaHeader = await getByUniqueSlug<IMetaHeader>({
          contentType: 'metaHeader',
          slug: 'default-meta-header',
          include: 1,
        });
      }

      const layoutProps: LayoutProps = Component.layoutProps || pageProps.layoutProps || {};

      let navigationBar;

      if (!layoutProps.showPoweredByInHeader && !isPerfPage) {
        const navigationBarData = await getByUniqueSlug<INavigationBar>({
          contentType: 'navigationBar',
          slug: 'default-nav-bar',
          include: 3,
        });
        navigationBar = processNavigationBarData(navigationBarData);
      }

      // Only override pageProps for pages with getInitialProps
      // https://github.com/vercel/next.js/discussions/11183

      if (mode !== 'browser' && getIsEmbedded(router)) {
        return { pageProps, metaHeader, mode, addHotjarScript, hasTracking };
      }
      let footerBar, footerPages;

      if (!isPerfPage) {
        const footerBarData = await getByUniqueSlug<IFooter>({
          contentType: 'footer',
          slug: 'default-footer',
          include: 3,
        });

        footerBar = processFooterBarData(footerBarData);

        const footerPagesData = (
          (
            await getClient().getEntries({
              content_type: 'linkSection',
              'fields.slug[in]': 'footer',
            })
          ).items[0] as ILinkSection
        ).fields?.pages;

        footerPages = this.footerPages || processStaticFooter(footerPagesData as IStaticLink[]);
        this.footerPages = footerPages;
      }

      const cookies = this.getCookies(ctx);

      return {
        pageProps,
        footerPages,
        layoutProps,
        cookies,
        // TODO: Change to layoutProps language selection after approval
        enableLanguageSelection: router.query.enableLanguageSelection === 'true',
        metaHeader,
        navigationBar,
        footerBar,
        mode,
        addHotjarScript,
        hasTracking,
      };
    } catch (error) {
      captureException(error, getScopeContext({ ctx }));
      log.error(
        { err: error, component: Component.name, url: ctx.req?.url },
        `Error in _app.tsx getting initial props for component ${Component.name}`,
      );

      if (ctx.res) {
        // @ts-expect-error FIXME: handle error types explicitly for improved error handling
        ctx.res.statusCode = error.status || 500;
      }
      return { error, pageProps: {} }; // if no pageProps, appWithTranslation crashes
    }
  }

  componentDidMount = async () => {
    const { router } = this.props;
    router.events.on('routeChangeStart', () => {
      this.loadTimeout = setTimeout(() => {
        this.setState({ isLoading: true });
      }, 100);
    });
    const loadComplete = (url: string) => {
      clearTimeout(this.loadTimeout);
      if (!EXCLUDE_FROM_TRACKING.has(url)) {
        ga.pageView(url);
      }
      this.setState({ isLoading: false });
    };
    router.events.on('routeChangeComplete', loadComplete);
    router.events.on('routeChangeError', loadComplete);

    sendEvent(EVENTS.webSessionStart.name, {});

    if (process.env.NODE_ENV !== 'test') {
      easterEgg();
    }

    smoothscroll.polyfill();

    if (!EXCLUDE_FROM_TRACKING.has(router.asPath)) {
      ga.pageView(router.asPath);
    }
  };

  componentWillUnmount = () => {
    clearTimeout(this.loadTimeout);
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureException(error, getScopeContext({ errorInfo }));
  }

  render() {
    const {
      Component,
      pageProps,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'footerPages' does not exist on type 'Rea... Remove this comment to see the full error message
      footerPages,
      layoutProps,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'cookies' does not exist on type 'Readonl... Remove this comment to see the full error message
      cookies,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'enableLanguageSelection' does not exist ... Remove this comment to see the full error message
      enableLanguageSelection,
      metaHeader,
      navigationBar,
      footerBar,
      addHotjarScript,
      error,
      hasTracking,
    } = this.props;

    if (error) {
      return <ErrorPage statusCode={'status' in error ? error.status : 500} />;
    }

    const {
      backgroundColor,
      hasMobileStaticButton,
      floatingHeader: floating,
      initiallyTransparentHeader,
    } = layoutProps || {};

    const isEmbedded = this.state.mode === 'app';

    return (
      <CookiesProvider cookies={isBrowser() ? undefined : cookies}>
        <Head>
          <link rel="preload" href={SharpGroteskBook20Woff2} as="font" crossOrigin="anonymous" />
          <link rel="preload" href={SharpGroteskBook25Woff2} as="font" crossOrigin="anonymous" />
          <link rel="preload" href={SharpGroteskMedium20Woff2} as="font" crossOrigin="anonymous" />
          <link rel="preload" href={SharpGroteskMedium25Woff2} as="font" crossOrigin="anonymous" />
          <link rel="preload" href={MilkywireHandRegularWoff2} as="font" crossOrigin="anonymous" />
          {/* Uncomment if use becomes common: */}
          {/* <link rel="preload" href={SharpGroteskBookItalic20Woff2} as="font" crossOrigin="anonymous" /> */}
          {/* and add import: import SharpGroteskBookItalic20Woff2 from 'assets/fonts/SharpGroteskBookItalic20.woff2';*/}
          <Normalize />
          <Fonts />
        </Head>
        {metaHeader && <MetaHeader metaHeader={metaHeader} />}
        {!metaHeader && <DefaultMetaHeader />}
        <UnsupportedBrowserModal />
        <AdjustTracking>
          <EmbeddedProvider value={isEmbedded}>
            <ReduxProvider store={store}>
              {isEmbedded ? (
                this.state.isLoading ? (
                  <Loader />
                ) : (
                  <Component {...pageProps} />
                )
              ) : (
                <Layout backgroundColor={backgroundColor}>
                  {layoutProps && (
                    <>
                      <CookieConsent />
                      <Header
                        navigationBar={navigationBar}
                        floating={floating}
                        initiallyTransparent={initiallyTransparentHeader}
                      />
                    </>
                  )}
                  {this.state.isLoading ? <Loader /> : <Component {...pageProps} />}
                  <Footer
                    footerPages={footerPages}
                    footerBar={footerBar}
                    showLanguageSelection={enableLanguageSelection}
                    hasMobileStaticButton={hasMobileStaticButton}
                  />
                </Layout>
              )}
            </ReduxProvider>
          </EmbeddedProvider>
        </AdjustTracking>
        {hasTracking && (
          <>
            <FacebookTracking />
            <SentryTracking />
            <UptimeTracking />
            <AmplitudeTracking />
            {addHotjarScript && <Hotjar />}
          </>
        )}
      </CookiesProvider>
    );
  }
}

const DEFAULT_META_TITLE = 'Milkywire – Support local charities to make an impact';
const DEFAULT_META_DESCRIPTION =
  'Meet the people changing the world. The ones that work tirelessly in the field every day to make a difference for their cause. Join them on their journey to make a long-standing impact.';
const DEFAULT_META_IMAGE = getMilkywireLogo();

const DefaultMetaHeader = () => (
  <Head>
    <title>{DEFAULT_META_TITLE}</title>
    <meta key="og:title" property="og:title" content={`Milkywire - Support local charities`} />
    <meta key="description" name="description" content={DEFAULT_META_DESCRIPTION} />
    <meta key="og:description" property="og:description" content={DEFAULT_META_DESCRIPTION} />
    <meta key="og:image" property="og:image" content={DEFAULT_META_IMAGE} />
    <meta key="og:image:width" property="og:image:width" content="1200" />
    <meta key="og:image:height" property="og:image:height" content="1200" />
    <meta name="viewport" content="initial-scale=1.0, width=device-width" />
    <script
      key="schema.org"
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'http://schema.org/',
          '@type': 'organization',
          name: DEFAULT_META_TITLE,
          image: DEFAULT_META_IMAGE,
          description: DEFAULT_META_DESCRIPTION,
        }),
      }}
    />
  </Head>
);

export default appWithTranslation(MyApp);
